import { CONFIG } from '../config';
import type { Bounds, ItemType } from '../types';

export class Item {
    public x: number;
    public y: number;
    public type: ItemType;
    public radius: number;
    public isAlive: boolean;
    public lifetime: number;
    public age: number;
    public rotation: number;
    public rotationSpeed: number;
    public pulsePhase: number;
    public pulseSpeed: number;

    constructor(x: number, y: number, type: ItemType) {
        this.x = x;
        this.y = y;
        this.type = type; // 'heart' or 'star'
        this.radius = type === 'heart' ? 20 : 15; // 하트는 20, 나머지는 15
        this.isAlive = true;
        this.lifetime = 10.0; // 10초 후 사라짐
        this.age = 0;

        // Animation
        this.rotation = 0;
        this.rotationSpeed = Math.PI * 2; // 1초에 1회전
        this.pulsePhase = 0;
        this.pulseSpeed = Math.PI * 2; // 1초에 1회 펄스
    }

    /**
     * 풀링을 위한 리셋 메서드
     */
    reset(x: number, y: number, type: ItemType): void {
        this.x = x;
        this.y = y;
        this.type = type;
        this.radius = type === 'heart' ? 20 : 15;
        this.isAlive = true;
        this.lifetime = 10.0;
        this.age = 0;
        this.rotation = 0;
        this.rotationSpeed = Math.PI * 2;
        this.pulsePhase = 0;
        this.pulseSpeed = Math.PI * 2;
    }

    update(deltaTime: number): void {
        this.age += deltaTime;

        // 수명이 다하면 제거
        if (this.age >= this.lifetime) {
            this.isAlive = false;
        }

        // 애니메이션 업데이트 (펄스만)
        this.pulsePhase += this.pulseSpeed * deltaTime;
    }

    render(ctx: CanvasRenderingContext2D, audioIntensity: number = 0): void {
        const pulseScale = 1 + Math.sin(this.pulsePhase) * 0.1; // 0.9 ~ 1.1
        const audioScale = 1 + audioIntensity * 0.3; // 음악에 반응 (최대 1.3배)
        const size = this.radius * pulseScale * audioScale;

        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);

        // 음악에 반응하는 lineWidth
        const lineWidth = 2 + audioIntensity * 1; // 최대 3px

        if (this.type === 'heart') {
            this.renderHeart(ctx, size, lineWidth);
        } else if (this.type === 'star') {
            this.renderStar(ctx, size, lineWidth);
        } else if (this.type === 'lightning') {
            this.renderLightning(ctx, size, lineWidth);
        } else if (this.type === 'clock') {
            this.renderClock(ctx, size, lineWidth);
        } else if (this.type === 'bomb') {
            this.renderBomb(ctx, size, lineWidth);
        } else if (this.type === 'magnet') {
            this.renderMagnet(ctx, size, lineWidth);
        }

        ctx.restore();
    }

    renderHeart(ctx: CanvasRenderingContext2D, size: number, lineWidth: number = 2): void {
        // 하트 모양 (흰색 아웃라인)
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = lineWidth;
        ctx.beginPath();

        // 하트 그리기 (간단한 버전)
        const scale = size / 10;
        ctx.moveTo(0, -3 * scale);
        ctx.bezierCurveTo(-5 * scale, -8 * scale, -10 * scale, -3 * scale, 0, 5 * scale);
        ctx.moveTo(0, -3 * scale);
        ctx.bezierCurveTo(5 * scale, -8 * scale, 10 * scale, -3 * scale, 0, 5 * scale);

        ctx.stroke();
    }

    renderStar(ctx: CanvasRenderingContext2D, size: number, lineWidth: number = 2): void {
        // 별 모양 (흰색 아웃라인)
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = lineWidth;
        ctx.beginPath();

        // 5각 별
        const spikes = 5;
        const outerRadius = size;
        const innerRadius = size * 0.5;

        for (let i = 0; i < spikes * 2; i++) {
            const radius = i % 2 === 0 ? outerRadius : innerRadius;
            const angle = (i * Math.PI) / spikes - Math.PI / 2;
            const x = Math.cos(angle) * radius;
            const y = Math.sin(angle) * radius;

            if (i === 0) {
                ctx.moveTo(x, y);
            } else {
                ctx.lineTo(x, y);
            }
        }

        ctx.closePath();
        ctx.stroke();
    }

    /**
     * 번개 아이템 렌더링 ⚡ (흰색 아웃라인)
     */
    renderLightning(ctx: CanvasRenderingContext2D, size: number, lineWidth: number = 2): void {
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = lineWidth;
        ctx.beginPath();

        const scale = size / 10;
        // 지그재그 번개 모양
        ctx.moveTo(2 * scale, -8 * scale);
        ctx.lineTo(-2 * scale, -2 * scale);
        ctx.lineTo(1 * scale, -2 * scale);
        ctx.lineTo(-3 * scale, 8 * scale);
        ctx.lineTo(0 * scale, 1 * scale);
        ctx.lineTo(-2 * scale, 1 * scale);
        ctx.closePath();

        ctx.stroke();
    }

    /**
     * 시계 아이템 렌더링 🕐 (흰색 아웃라인)
     */
    renderClock(ctx: CanvasRenderingContext2D, size: number, lineWidth: number = 2): void {
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = lineWidth;

        // 원형 시계
        ctx.beginPath();
        ctx.arc(0, 0, size * 0.8, 0, Math.PI * 2);
        ctx.stroke();

        // 시계 바늘 (시침)
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(0, -size * 0.4);
        ctx.stroke();

        // 분침
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(size * 0.5, 0);
        ctx.stroke();

        // 중심점 (작은 원)
        ctx.beginPath();
        ctx.arc(0, 0, size * 0.15, 0, Math.PI * 2);
        ctx.stroke();
    }

    /**
     * 폭탄 아이템 렌더링 💣 (흰색 아웃라인, 디테일 강화)
     */
    renderBomb(ctx: CanvasRenderingContext2D, size: number, lineWidth: number = 2): void {
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = lineWidth;

        // 폭탄 몸체 (약간 찌그러진 원형 - 클래식 폭탄 스타일)
        ctx.beginPath();
        ctx.ellipse(0, size * 0.15, size * 0.65, size * 0.7, 0, 0, Math.PI * 2);
        ctx.stroke();

        // 폭탄 상단 목 부분 (사다리꼴)
        ctx.beginPath();
        ctx.moveTo(-size * 0.25, -size * 0.5);
        ctx.lineTo(-size * 0.15, -size * 0.7);
        ctx.lineTo(size * 0.15, -size * 0.7);
        ctx.lineTo(size * 0.25, -size * 0.5);
        ctx.stroke();

        // 폭탄 목 상단 고리
        ctx.beginPath();
        ctx.arc(0, -size * 0.75, size * 0.12, 0, Math.PI * 2);
        ctx.stroke();

        // 도화선 (곡선으로 더 자연스럽게)
        ctx.beginPath();
        ctx.moveTo(0, -size * 0.87);
        ctx.bezierCurveTo(
            size * 0.2, -size * 1.0,
            size * 0.35, -size * 1.05,
            size * 0.25, -size * 1.25
        );
        ctx.stroke();

        // 불꽃 스파크 효과 (펄스에 따라 크기 변화)
        const sparkSize = size * 0.15 * (1 + Math.sin(this.pulsePhase * 3) * 0.3);
        const sparkX = size * 0.25;
        const sparkY = -size * 1.25;

        // 중심 불꽃
        ctx.beginPath();
        ctx.arc(sparkX, sparkY, sparkSize, 0, Math.PI * 2);
        ctx.stroke();

        // 불꽃 스파크 라인들 (6방향)
        for (let i = 0; i < 6; i++) {
            const angle = (i * Math.PI) / 3 + this.pulsePhase * 0.5;
            const innerR = sparkSize * 1.2;
            const outerR = sparkSize * 2.2;
            ctx.beginPath();
            ctx.moveTo(
                sparkX + Math.cos(angle) * innerR,
                sparkY + Math.sin(angle) * innerR
            );
            ctx.lineTo(
                sparkX + Math.cos(angle) * outerR,
                sparkY + Math.sin(angle) * outerR
            );
            ctx.stroke();
        }

        // 폭탄 몸체 하이라이트 (광택 효과)
        ctx.beginPath();
        ctx.arc(-size * 0.25, -size * 0.1, size * 0.15, 0, Math.PI * 2);
        ctx.stroke();

        // 폭탄 몸체 밴드 (가로 줄)
        ctx.beginPath();
        ctx.moveTo(-size * 0.6, size * 0.3);
        ctx.quadraticCurveTo(0, size * 0.4, size * 0.6, size * 0.3);
        ctx.stroke();
    }

    /**
     * 자석 아이템 렌더링 🧲 (흰색 아웃라인)
     */
    renderMagnet(ctx: CanvasRenderingContext2D, size: number, lineWidth: number = 2): void {
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = lineWidth;

        // U자형 자석 (위쪽 곡선)
        ctx.beginPath();
        ctx.arc(0, 0, size * 0.6, Math.PI, 0, false);
        ctx.stroke();

        // 왼쪽 다리
        ctx.beginPath();
        ctx.moveTo(-size * 0.6, 0);
        ctx.lineTo(-size * 0.6, size * 0.7);
        ctx.stroke();

        // 오른쪽 다리
        ctx.beginPath();
        ctx.moveTo(size * 0.6, 0);
        ctx.lineTo(size * 0.6, size * 0.7);
        ctx.stroke();

        // 다리 끝 가로선 (왼쪽)
        ctx.beginPath();
        ctx.moveTo(-size * 0.8, size * 0.7);
        ctx.lineTo(-size * 0.4, size * 0.7);
        ctx.stroke();

        // 다리 끝 가로선 (오른쪽)
        ctx.beginPath();
        ctx.moveTo(size * 0.4, size * 0.7);
        ctx.lineTo(size * 0.8, size * 0.7);
        ctx.stroke();
    }

    getBounds(): Bounds {
        return {
            x: this.x,
            y: this.y,
            radius: this.radius
        };
    }
}
